import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import Label from '../../../components/label';
import relativeTime from '../../../utils/relativeTime';
import {
  CALENDARS_HEADING,
  CALENDARS_NOTE,
  CALENDAR_PROBLEMS,
  GRANT_WRITE_NOTE,
  NEEDS_SHARED_PERMISSION,
  WRITE_LOST_NOTE,
  brokenBanner,
  failingBanner,
} from './calendarCopy';

// A shared calendar that a `write_owned_only` connection cannot read: the account
// granted events but not free/busy, so only owned calendars can be checked.
function needsSharedPermission(calendar, connection) {
  return (
    !calendar.canTick &&
    !calendar.blockedBy &&
    calendar.accessRole !== 'owner' &&
    connection.permissions?.freebusy === false
  );
}

function captionsOf(calendar, connection) {
  const captions = [];
  if (calendar.primary) captions.push('Primary');
  if (calendar.accessRole !== 'owner') captions.push('Shared with you');
  if (calendar.blockedBy) captions.push(`Checked through ${calendar.blockedBy}`);
  if (needsSharedPermission(calendar, connection)) captions.push(NEEDS_SHARED_PERMISSION);
  if (CALENDAR_PROBLEMS[calendar.problem]) captions.push(CALENDAR_PROBLEMS[calendar.problem]);
  return captions;
}

// One connected Google account. `mode` is the view's mode: `on` shows everything,
// `paused` shows the calendars without controls, `disconnect_only` shows the email and
// Disconnect. Disconnect works in every mode (spec 9.2).
export default function ConnectionCard({
  connection,
  zone,
  mode,
  writesAllowed,
  isSaving,
  isStarting,
  onToggle,
  onReconnect,
  onGrant,
  onDisconnect,
}) {
  const isOn = mode === 'on';
  const calendars = connection.calendars || [];
  const lastChecked = relativeTime(connection.lastCheckedAt, zone);
  const isBroken = connection.status === 'broken';
  const canGrantWrites = isOn && writesAllowed;
  const showGrantWrite =
    canGrantWrites && connection.permissions?.eventsOwned === false && connection.problem !== 'write_lost';
  const showGrantShared = isOn && calendars.some((calendar) => needsSharedPermission(calendar, connection));

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1">{connection.accountEmail}</Typography>
          {mode !== 'disconnect_only' && lastChecked && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Last checked {lastChecked}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          {mode !== 'disconnect_only' && (
            <Label color={isBroken ? 'error' : 'success'}>{isBroken ? 'Reconnect needed' : 'Connected'}</Label>
          )}
          <Button variant="outlined" color="error" onClick={onDisconnect}>
            Disconnect
          </Button>
        </Stack>
      </Stack>

      {isOn && connection.banner === 'failing' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {failingBanner(connection.lastCheckedAt, zone)}
        </Alert>
      )}
      {isOn && connection.banner === 'broken' && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" disabled={isStarting} onClick={onReconnect}>
              Reconnect
            </Button>
          }
        >
          {brokenBanner(connection.copyValidUntil, zone)}
        </Alert>
      )}
      {/* Hidden while writes are off on the server (spec 9.2, decided 2026-10-16). */}
      {canGrantWrites && connection.problem === 'write_lost' && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" disabled={isStarting} onClick={onGrant}>
              Grant permission
            </Button>
          }
        >
          {WRITE_LOST_NOTE}
        </Alert>
      )}
      {showGrantWrite && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" disabled={isStarting} onClick={onGrant}>
              Grant permission
            </Button>
          }
        >
          {GRANT_WRITE_NOTE}
        </Alert>
      )}

      {mode !== 'disconnect_only' && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2">{CALENDARS_HEADING}</Typography>
          {isOn && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {CALENDARS_NOTE}
            </Typography>
          )}
          <Stack sx={{ mt: 1 }}>
            {calendars.map((calendar) => {
              const isDisabled =
                !isOn || isSaving || (!calendar.ticked && (!calendar.canTick || Boolean(calendar.blockedBy)));
              const captions = captionsOf(calendar, connection);
              return (
                <FormControlLabel
                  key={calendar.id}
                  disabled={isDisabled}
                  control={<Checkbox checked={Boolean(calendar.ticked)} onChange={() => onToggle(calendar)} />}
                  label={
                    <Stack direction={{ xs: 'column', sm: 'row' }} columnGap={2}>
                      <Typography variant="body2">{calendar.name}</Typography>
                      {captions.length > 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {captions.join(', ')}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
          {showGrantShared && (
            <Button size="small" sx={{ mt: 1 }} disabled={isStarting} onClick={onGrant}>
              Grant permission
            </Button>
          )}
        </Box>
      )}
    </Card>
  );
}
